// Admin/internal-staff Concierge board: a drag-reorderable grid of athlete
// tiles (photo, name, school, last-managed date) with per-user saved order.
// Manual order wins; stale/expiring render as advisory corner badges.
// Search/sort/filter are a temporary view overlay that never touches the
// saved order — drag is disabled whenever an overlay control is active.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { reorderTiles, orderedIds, formatLastManaged, filterTiles, sortTiles } from '../lib/conciergeBoardModel';
import '../components/Layout.css';
import AdminPage from '../components/AdminShell';
import TilePhoto from '../components/TilePhoto';
import './ConciergePage.css';
import '../components/athleteEditor/AthleteEditor.css';

const SORT_OPTIONS = [
  { value: 'manual', label: 'Manual', hint: null },
  { value: 'stale', label: 'Most stale first', hint: 'Sorted by most stale' },
  { value: 'recent', label: 'Recently managed', hint: 'Sorted by recently managed' },
  { value: 'expiring', label: 'Soonest expiring', hint: 'Sorted by soonest expiring' },
  { value: 'todos', label: 'Most overdue to-dos', hint: 'Sorted by overdue to-dos' },
  { value: 'name', label: 'Name A–Z', hint: 'Sorted by name' },
];

function initialsOf(name) {
  const parts = (name || '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '—';
}

export default function ConciergeBoardPage() {
  const navigate = useNavigate();
  const { API_URL, user } = useAuth();
  // Partners only ever see their own clients, so the filter is staff-only.
  const canFilterOwner = user?.role === 'admin' || user?.role === 'internal_staff';
  const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  // Recently managed is the default view (David, 2026-10-30); Clear goes to Manual so tiles can be dragged.
  const [sortMode, setSortMode] = useState('recent');
  const [stale, setStale] = useState(false);
  const [expiring, setExpiring] = useState(false);
  const [overdue, setOverdue] = useState(false);
  // Staff default to ProZpects-managed athletes only; unchecked shows everyone.
  const [prozpectsOnly, setProzpectsOnly] = useState(true);
  const ownerFilterOn = canFilterOwner && prozpectsOnly;

  const dragId = useRef(null);
  const saveTimer = useRef(null);
  const pendingOrder = useRef(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/concierge/board`, cfg)
      .then(res => setTiles(Array.isArray(res.data) ? res.data : []))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load the concierge board.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [API_URL]);

  const sendOrder = (ids, onFail) => {
    axios.put(`${API_URL}/concierge/board/order`, { athlete_user_ids: ids }, cfg).catch(onFail);
  };

  // On unmount, flush a pending reorder immediately so a quick drag-then-navigate
  // (clicking a tile within the debounce window) isn't silently dropped.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (pendingOrder.current) sendOrder(pendingOrder.current, () => {});
    // eslint-disable-next-line
  }, []);

  const persist = (next) => {
    const ids = orderedIds(next);
    pendingOrder.current = ids;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      pendingOrder.current = null;
      sendOrder(ids, () => setError('Failed to save the new order.'));
    }, 600);
  };

  const onDrop = (targetId) => {
    const from = dragId.current;
    dragId.current = null;
    if (from == null) return;
    setTiles(prev => {
      const next = reorderTiles(prev, from, targetId);
      if (next !== prev) persist(next);
      return next;
    });
  };

  const isOverlay = sortMode !== 'manual' || stale || expiring || overdue || search.trim() !== '';
  const view = sortTiles(filterTiles(tiles, { search, stale, expiring, overdue, prozpectsOnly: ownerFilterOn }), sortMode);

  const clearControls = () => {
    setSearch('');
    setSortMode('manual');
    setStale(false);
    setExpiring(false);
    setOverdue(false);
  };

  const hint = isOverlay
    ? `${(SORT_OPTIONS.find(o => o.value === sortMode) || {}).hint || 'Filtered'} · clear to rearrange`
    : 'Drag to arrange your board · your order is private to you';

  return (
    <AdminPage active="concierge" title="Concierge Board" subtitle={hint}>
      <div className="cb-wrap">
        <div className="cb-links">
          <button type="button" className="cb-controls__clear" onClick={() => navigate('/concierge/todos')}>
            My open items →
          </button>
        </div>
        {error && <div className="cn-inline-msg cn-inline-msg--err" style={{ margin: '0 0 16px' }}>{error}</div>}

        {!loading && tiles.length > 0 && (
          <div className="cb-controls">
            {canFilterOwner && (
              <label className="cb-controls__sortlabel" data-testid="prozpects-only-filter">
                <input
                  type="checkbox"
                  checked={prozpectsOnly}
                  onChange={e => setProzpectsOnly(e.target.checked)}
                />
                <span className="cb-controls__sortlabel-text">ProZpects-managed only</span>
              </label>
            )}
            <input
              className="cb-controls__search"
              type="search"
              placeholder="Search name or school…"
              aria-label="Search concierge athletes by name or school"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <label className="cb-controls__sortlabel">
              <span className="cb-controls__sortlabel-text">Sort</span>
              <select
                className="cb-controls__sort"
                value={sortMode}
                onChange={e => setSortMode(e.target.value)}
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <button
              type="button"
              className="cb-chip"
              aria-pressed={stale}
              onClick={() => setStale(v => !v)}
            >Stale</button>
            <button
              type="button"
              className="cb-chip"
              aria-pressed={expiring}
              onClick={() => setExpiring(v => !v)}
            >Expiring</button>
            <button
              type="button"
              className="cb-chip"
              aria-pressed={overdue}
              onClick={() => setOverdue(v => !v)}
            >Overdue to-dos</button>
            <span className="cb-controls__count" aria-live="polite">
              {(isOverlay || ownerFilterOn) ? `Showing ${view.length} of ${tiles.length}` : ''}
            </span>
            {isOverlay && (
              <button type="button" className="cb-controls__clear" onClick={clearControls}>Clear</button>
            )}
          </div>
        )}

        {loading ? (
          <div className="cn-loading">Loading…</div>
        ) : tiles.length === 0 ? (
          <div className="cn-empty">No concierge athletes yet.</div>
        ) : view.length === 0 ? (
          <div className="cn-empty">No athletes match your filters.</div>
        ) : (
          <div className="cb-grid">
            {view.map(t => (
              <div
                key={t.user_id}
                className="cb-tile"
                draggable={!isOverlay}
                onDragStart={() => { if (!isOverlay) dragId.current = t.user_id; }}
                onDragOver={e => { if (!isOverlay) e.preventDefault(); }}
                onDrop={() => { if (!isOverlay) onDrop(t.user_id); }}
                onClick={() => navigate(`/athletes/${t.user_id}/concierge`)}
                role="button"
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/athletes/${t.user_id}/concierge`); } }}
              >
                <div className="cb-tile__badges">
                  {t.is_expiring && <span className="cb-badge cb-badge--expiring">Expiring</span>}
                  {t.is_stale && <span className="cb-badge cb-badge--stale">Stale</span>}
                </div>
                {t.photo_thumb
                  ? <TilePhoto className="cb-tile__photo" thumb={t.photo_thumb} full={t.photo_url} alt={t.name} draggable={false} />
                  : <div className="cb-tile__photo cb-tile__photo--initials" aria-hidden="true">{initialsOf(t.name)}</div>}
                <div className="cb-tile__name">{t.name || '(unnamed)'}</div>
                <div className="cb-tile__school">{t.school || '—'}</div>
                <div className="cb-tile__managed">Last managed: {formatLastManaged(t.last_managed_at)}</div>
                {(t.todo_overdue > 0 || t.todo_waiting > 0) && (
                  <div className="cb-tile__todo" data-testid={`tile-todo-${t.user_id}`}>
                    {t.todo_overdue > 0 && <span className="cb-tile__todo-overdue">{t.todo_overdue} overdue</span>}
                    {t.todo_overdue > 0 && t.todo_waiting > 0 && ' · '}
                    {t.todo_waiting > 0 && <span>{t.todo_waiting} waiting</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminPage>
  );
}
